import type { VehicleGetAPI } from '@hero/api';
import { useApi } from '@hero/context';
import type {
  UseVehicleApi,
  UseVehicleListApi,
  UseVehicleCreateApi,
  UseVehicleUpdateApi,
  UseMyVehicleApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get single vehicle by ID. Pass id for normal use; pass undefined to get runQuery only.
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn.
 *
 * Hook API: {@link UseVehicleApi}
 */
export const useVehicleApi: UseVehicleApi = (id, apiConfig) => {
  const api = useApi('carrier');
  return useQueryAPI({
    entity: 'vehicle',
    apiFn: api.vehicles.getVehicle,
    query: id != null ? ({ id: String(id) } as VehicleGetAPI['req']) : undefined,
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * List vehicles with pagination (skip/limit).
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn.
 *
 * Hook API: {@link UseVehicleListApi}
 */
export const useVehicleListApi: UseVehicleListApi = (query, apiConfig) => {
  const api = useApi('carrier');
  return useInfiniteQueryAPI({
    entity: 'vehicle',
    apiFn: api.vehicles.getVehicles,
    query,
    apiConfig,
    isEnabled: () => true,
  });
};

/**
 * Create vehicle. Handles cache invalidation and optional onSuccess/onError.
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn.
 *
 * Hook API: {@link UseVehicleCreateApi}
 */
export const useVehicleCreateApi: UseVehicleCreateApi = apiConfig => {
  const api = useApi('carrier');
  return useMutationAPI({
    entity: 'vehicle',
    apiFn: api.vehicles.createVehicle,
    apiConfig,
  });
};

/**
 * Update vehicle. Handles cache invalidation and optional onSuccess/onError.
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn.
 *
 * Hook API: {@link UseVehicleUpdateApi}
 */
export const useVehicleUpdateApi: UseVehicleUpdateApi = apiConfig => {
  const api = useApi('carrier');
  return useMutationAPI({
    entity: 'vehicle',
    apiFn: api.vehicles.updateVehicle,
    apiConfig,
  });
};

/**
 * Get current user's vehicle (for driver-type users / freelancers).
 * API from ApiProvider (useApi('carrier')); pass apiConfig to override apiFn.
 *
 * Hook API: {@link UseMyVehicleApi}
 */
export const useMyVehicleApi: UseMyVehicleApi = apiConfig => {
  const api = useApi('carrier');
  return useQueryAPI({
    entity: 'vehicle',
    apiFn: api.vehicles.getMyVehicle,
    query: undefined,
    apiConfig,
  });
};
